import React, { useMemo } from "react";
import classNames from "classnames";
import { Flex, Icon, Text, Button } from "@jsq/ds/core";
import { FormRadioButton, FormRadioGroup, Form, SubmitButton } from "@jsq/ds/forms";
import { FieldGroup } from "../../../../common/components/Form/Layout/FieldGroup";
import { PaymentMethod } from "../../../../common/enums";
import { toDbId } from "../../../../utils/graphql_utils";
import { type RedactedPaymentPref } from "../../common/types";
import { getPaymentMethod } from "../../common/utils";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { ExistingPaymentPrefDisplay } from "../ExistingPaymentPrefDisplay";
import { type ChildComponentProps } from "../types";
import {
  CREATE_NEW_PAYMENT_OPTION,
  SelectAvailablePaymentPrefsFormInitialValues,
  SelectAvailablePaymentPrefsFormSchema,
} from "./constants";
import { horizontalLine, paymentPrefDisplayCard } from "./styles_vanilla.css";
import {
  type SelectAvailablePaymentPrefsFormInitialValuesType,
  type SelectAvailablePaymentPrefsFormProps,
} from "./types";

export const SelectAvailablePaymentPrefsForm: React.FC<
  SelectAvailablePaymentPrefsFormProps
> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName = "Cancel",
  displayPaymentPrefPositions = true,
}) => {
  const {
    existingPaymentPrefs,
    paymentMethods,
    sourcePaymentPrefId,
    setIsExistingPaymentPref,
    setPaymentPrefId,
    setPaymentPrefDetail,
    setSelectedPaymentPref,
    setSelectedPaymentMethod,
    paymentPrefId,
    SelectAvailablePaymentPrefsFormHeaderComponent,
  } = useConsolidatedPaymentPrefContext();
  // We need to filter which payment prefs are available based on the allowed payment methods.
  // Here we are creating an array of all available paymentprefs which includes only payment prefs whose payment
  // methods are allowed. This is not a computationally expensive operation but is memoized because this data
  // should rarely change between renders.
  const availablePaymentPrefs = useMemo(() => {
    const PaymentMethods = Object.values(PaymentMethod);
    return (
      existingPaymentPrefs?.filter((paymentPref) => {
        const paymentPrefMethod = paymentPref.paymentMethod as PaymentMethod;
        if (paymentPref.id === sourcePaymentPrefId) {
          // user shouldn't be able to select the source payment pref
          return false;
        }
        if (paymentPrefMethod && PaymentMethods.includes(paymentPrefMethod)) {
          return paymentMethods?.[paymentPrefMethod].isAvailable;
        }
        return false;
      }) ?? []
    );
  }, [existingPaymentPrefs, paymentMethods, sourcePaymentPrefId]);

  // Map of payment pref ids to their respective objects.
  // Makes it easier to retrieve the payment pref later on given the payment pref id.
  const paymentPrefIdsToObjects: Record<string, RedactedPaymentPref | null> =
    Object.fromEntries((availablePaymentPrefs ?? []).map((x) => [x.id, x ?? null]));

  const handleFormSubmission = (values: SelectAvailablePaymentPrefsFormInitialValuesType) => {
    // When the default option is chosen no new value is set. Which is why we check for undefined here.
    if (
      values.availablePaymentPrefsOption === undefined ||
      values.availablePaymentPrefsOption === CREATE_NEW_PAYMENT_OPTION
    ) {
      setIsExistingPaymentPref(false);
      setPaymentPrefId(undefined);
      setPaymentPrefDetail(undefined);
      setSelectedPaymentPref(undefined);
    } else {
      const selectedPref = paymentPrefIdsToObjects[values.availablePaymentPrefsOption];

      if (
        selectedPref &&
        Object.values(PaymentMethod).includes(getPaymentMethod(selectedPref))
      ) {
        setIsExistingPaymentPref(true);
        setPaymentPrefId(values.availablePaymentPrefsOption);
        // TODO: fix casing https://junipersquare.atlassian.net/browse/FF-3245
        setSelectedPaymentMethod(selectedPref.paymentMethod as PaymentMethod);
        setSelectedPaymentPref(selectedPref);
        setPaymentPrefDetail(undefined);
      } else {
        throw new Error("selected payment pref not found");
      }
    }
    onContinueClick();
  };

  const initialFormValues = paymentPrefId
    ? {
        availablePaymentPrefsOption: paymentPrefId,
      }
    : SelectAvailablePaymentPrefsFormInitialValues;

  return (
    <Form
      onSubmit={handleFormSubmission}
      initialValues={initialFormValues}
      validationSchema={SelectAvailablePaymentPrefsFormSchema}
      submitOnlyChangedValues={false}
    >
      <FieldGroup title="Available payment instructions" meta={{ headerType: "h3" }}>
        {!!SelectAvailablePaymentPrefsFormHeaderComponent && (
          <SelectAvailablePaymentPrefsFormHeaderComponent />
        )}
        <FormRadioGroup name="availablePaymentPrefsOption">
          <FormRadioButton key="CREATE_NEW_PAYMENT_OPTION" value={CREATE_NEW_PAYMENT_OPTION}>
            <Flex flexDirection="column">
              <Flex alignItems="center" justifyContent="space-between" gap={8}>
                <Icon type="plus" variant="solid" />
                <Text variant="strong-md">Create new payment instructions</Text>
              </Flex>
            </Flex>
          </FormRadioButton>
          {availablePaymentPrefs?.map((paymentPref) => {
            return (
              <div key={`paymentPref-${paymentPref.id}`}>
                <hr className={classNames(horizontalLine, "stacked-md")} />
                <FormRadioButton
                  key={`payment-pref-${toDbId(paymentPref.id)}`}
                  value={paymentPref.id}
                  className="stacked-md"
                >
                  {paymentPref && (
                    <div className={paymentPrefDisplayCard}>
                      <ExistingPaymentPrefDisplay
                        displayPositions={displayPaymentPrefPositions}
                        displayRevealDetails
                        paymentPref={paymentPref}
                      />
                    </div>
                  )}
                </FormRadioButton>
              </div>
            );
          })}
        </FormRadioGroup>
      </FieldGroup>
      <div>
        <Flex flexDirection="row-reverse" gap={8}>
          <SubmitButton>Continue</SubmitButton>
          <Button variant="neutral" onClick={onBackClick}>
            {onBackButtonName}
          </Button>
        </Flex>
      </div>
    </Form>
  );
};

/* used by the GP flow */
export const SelectAvailablePaymentPrefsFormWithPositions: React.FC<ChildComponentProps> = (
  props,
) => {
  return (
    <SelectAvailablePaymentPrefsForm
      displayPaymentPrefPositions
      onContinueClick={props.onContinueClick}
      onBackClick={props.onBackClick}
      onBackButtonName={props.onBackButtonName}
    />
  );
};

/* used by the LP portal 2.0 flow */
export const SelectAvailablePaymentPrefsFormWithoutPositions: React.FC<ChildComponentProps> = (
  props,
) => {
  return (
    <SelectAvailablePaymentPrefsForm
      displayPaymentPrefPositions={false}
      onContinueClick={props.onContinueClick}
      onBackClick={props.onBackClick}
      onBackButtonName={props.onBackButtonName}
    />
  );
};
